import React, { useEffect } from 'react';
import { View, Modal, StyleSheet, Pressable, useWindowDimensions } from 'react-native';
import { Text } from '~/components/core';
import ScreenWrapper from '../Theme/ScreenWrapper';
import { useStyles } from '~/hooks';
import { Theme } from '~/theme';
import { isDesktop, scaleSize } from '~/constants/scale';
import { global } from '~/constants/global';
import { FONT_SIZE_LS } from '~/constants';
import { Entypo, FontAwesome5, Ionicons } from '~/components/core/Icons';

export type ConfirmationMessageProps = {
    visible: boolean;
    text: string;
    setVisible: Function;
    error?: boolean;
    timeout?: number;
    // Optional tap action on the message box itself (the backdrop still just dismisses),
    // e.g. the AI-strategy popup navigating to the finished strategy.
    onPress?: () => void;
};

const ConfirmationMessage = ({ visible, text, setVisible, error, timeout, onPress }: ConfirmationMessageProps) => {
    const styles = useStyles(stylesGenerator);
    const dimensions = useWindowDimensions();

    useEffect(() => {
        if (visible && (error === undefined || error === false)) {
            setTimeout(
                () => {
                    setVisible(false);
                },
                timeout ? timeout : 5000
            );
        }
    }, [error, setVisible, visible]);
    return (
        <Modal
            statusBarTranslucent={true}
            animationType="slide"
            transparent={true}
            visible={visible}
            onRequestClose={() => {
                setVisible(false);
            }}
        >
            <Pressable onPress={() => setVisible(false)} style={styles.fullScreen}>
                <Pressable
                    onPress={() => {
                        setVisible(false);
                        onPress?.();
                    }}
                    style={[
                        styles.container,
                        {
                            top:
                                dimensions.height -
                                scaleSize(
                                    global.CONFIRMATION_MESSAGE_HEIGHT + (!isDesktop() && text.length > 120 ? 40 : 0)
                                ) -
                                scaleSize(isDesktop() ? global.HOME_TABS_HEIGHT_DESKTOP : global.HOME_TABS_HEIGHT),
                            height: scaleSize(
                                !isDesktop() && text.length > 120
                                    ? global.CONFIRMATION_MESSAGE_HEIGHT + 40
                                    : global.CONFIRMATION_MESSAGE_HEIGHT
                            ),
                        },
                    ]}
                >
                    <ScreenWrapper style={styles.screen}>
                        <View style={styles.content}>
                            <View style={styles.closeButton}>
                                <Ionicons name="close" size={scaleSize(14)} color={'white'} />
                            </View>
                            <View style={styles.messageContainer}>
                                <View style={[styles.checkContainer, styles.checkContainerSuccess]}>
                                    {error === false ? (
                                        <Entypo
                                            name="check"
                                            style={styles.messageIconColorSuccess}
                                            size={scaleSize(26)}
                                        />
                                    ) : (
                                        <FontAwesome5
                                            name="exclamation"
                                            style={styles.messageIconColor}
                                            size={scaleSize(15)}
                                        />
                                    )}
                                </View>
                                <Text style={styles.messageText} fontType="LATO_BOLD">
                                    {text}
                                </Text>
                            </View>
                        </View>
                    </ScreenWrapper>
                </Pressable>
            </Pressable>
        </Modal>
    );
};

export default ConfirmationMessage;

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        messageIconColorSuccess: { color: theme.tabIconSelected },
        messageIconColor: { color: theme.background_message },
        fullScreen: { width: '100%', height: '100%' },
        closeButton: {
            alignItems: 'flex-end',
            marginTop: scaleSize(5),
            marginRight: scaleSize(5),
        },
        checkContainer: {
            height: scaleSize(40),
            width: scaleSize(40),
            borderRadius: scaleSize(20),
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: scaleSize(12),
        },
        checkContainerSuccess: { backgroundColor: theme.white },
        checkContainerError: { backgroundColor: 'red' },
        messageContainer: {
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            paddingHorizontal: scaleSize(7),
        },
        messageText: {
            fontSize: FONT_SIZE_LS,
            color: theme.white,
            textAlign: 'center',
        },
        screen: {
            width: '100%',
        },
        container: {
            borderTopLeftRadius: scaleSize(5),
            borderTopEndRadius: scaleSize(5),
            left: 0,
            position: 'absolute',
            width: '100%',
        },
        content: {
            width: '100%',
            flex: 1,
            borderTopLeftRadius: scaleSize(5),
            borderTopEndRadius: scaleSize(5),
            backgroundColor: theme.background_message,
        },
    });
